import * as React from "react";
import { useSearchParams } from "react-router-dom";
import { useSinkActions, useSinkState } from "context/";
import type { SurveyDateType, SurveyListFilters } from "context/sink";

function toSortedUniqueInts(values: number[]): number[] {
  return Array.from(new Set(values.filter((v) => Number.isFinite(v)))).sort(
    (a, b) => a - b
  );
}

function toSortedUniqueStrings(values: string[]): string[] {
  return Array.from(
    new Set(values.map((v) => v.trim()).filter((v) => v.length > 0))
  ).sort();
}

function arraysEqual<T>(a: T[], b: T[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

function isValidDateType(value: string | null): value is SurveyDateType {
  return (
    value === "flight-date" ||
    value === "locked-date" ||
    value === "created-date" ||
    value === "submitted-date" ||
    value === "completed-date"
  );
}

function normalizeFiltersForCompare(filters: SurveyListFilters): SurveyListFilters {
  return {
    ...filters,
    searchString:
      filters.searchString && filters.searchString.trim().length > 0
        ? filters.searchString
        : null,
    selectedTagIds: toSortedUniqueInts(filters.selectedTagIds ?? []),
    selectedFolderIds: toSortedUniqueInts(filters.selectedFolderIds ?? []),
    selectedSiteIds: toSortedUniqueInts(filters.selectedSiteIds ?? []),
    selectedStatuses: toSortedUniqueStrings(filters.selectedStatuses ?? []),
    selectedOrderTypes: toSortedUniqueStrings(filters.selectedOrderTypes ?? []),
    selectedCreatedByUserIds: toSortedUniqueInts(filters.selectedCreatedByUserIds ?? []),
    selectedLockedByUserIds: toSortedUniqueInts(filters.selectedLockedByUserIds ?? []),
  };
}

function parseCommaSeparatedInts(raw: string | null): number[] {
  if (!raw) return [];
  const values = raw
    .split(",")
    .map((v) => parseInt(v, 10))
    .filter((n) => !Number.isNaN(n));
  return toSortedUniqueInts(values);
}

function parseCommaSeparatedStrings(raw: string | null): string[] {
  if (!raw) return [];
  return toSortedUniqueStrings(raw.split(","));
}

function parseRepeatedStrings(params: URLSearchParams, key: string): string[] {
  return toSortedUniqueStrings(params.getAll(key));
}

function setRepeatedParam(params: URLSearchParams, key: string, values: string[]) {
  params.delete(key);
  toSortedUniqueStrings(values).forEach((v) => params.append(key, v));
}

function firstNonEmptyString(raw: string | null): string | null {
  if (!raw) return null;
  const v = raw.trim();
  return v.length > 0 ? v : null;
}

function isIsoDateOnly(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value);
}

export function searchParamsToFilters(params: URLSearchParams): SurveyListFilters {
  const dateTypeRaw = params.get("dateType");
  return {
    searchString: firstNonEmptyString(params.get("q")),
    selectedTagIds: parseCommaSeparatedInts(params.get("tags")),
    selectedFolderIds: parseCommaSeparatedInts(params.get("folders")),
    selectedSiteIds: parseCommaSeparatedInts(params.get("sites")),
    selectedStatuses: parseCommaSeparatedStrings(params.get("status")),
    selectedOrderTypes: parseRepeatedStrings(params, "orderTypes"),
    selectedCreatedByUserIds: parseCommaSeparatedInts(params.get("createdBy")),
    selectedLockedByUserIds: parseCommaSeparatedInts(params.get("lockedBy")),
    showOnlyArchived: params.get("archived") === "1",
    dateType: isValidDateType(dateTypeRaw)
      ? dateTypeRaw
      : "created-date",
    dateRange: (() => {
      const from = firstNonEmptyString(params.get("from"));
      const to = firstNonEmptyString(params.get("to"));
      return from && to && isIsoDateOnly(from) && isIsoDateOnly(to)
        ? { startDate: from, endDate: to }
        : { startDate: "", endDate: "" };
    })(),
  };
}

function setCommaSeparatedParam(
  params: URLSearchParams,
  key: string,
  values: (string | number)[] | undefined
) {
  const normalized = values
    ? values
        .map((v) => String(v).trim())
        .filter((v) => v.length > 0)
    : [];
  if (normalized.length === 0) {
    params.delete(key);
    return;
  }
  params.set(key, normalized.join(","));
}

function setStringParam(params: URLSearchParams, key: string, value: string | null | undefined) {
  const v = value != null ? String(value).trim() : "";
  if (!v) {
    params.delete(key);
    return;
  }
  params.set(key, v);
}

export function filtersToSearchParams(filters: SurveyListFilters): URLSearchParams {
  const params = new URLSearchParams();
  const normalized = normalizeFiltersForCompare(filters);

  setStringParam(params, "q", normalized.searchString);
  setCommaSeparatedParam(params, "tags", normalized.selectedTagIds);
  setCommaSeparatedParam(params, "folders", normalized.selectedFolderIds);
  setCommaSeparatedParam(params, "sites", normalized.selectedSiteIds);
  setCommaSeparatedParam(params, "status", normalized.selectedStatuses);
  setRepeatedParam(params, "orderTypes", normalized.selectedOrderTypes);
  setCommaSeparatedParam(params, "createdBy", normalized.selectedCreatedByUserIds);
  setCommaSeparatedParam(params, "lockedBy", normalized.selectedLockedByUserIds);

  if (normalized.dateType && normalized.dateType !== "created-date") {
    params.set("dateType", normalized.dateType);
  }

  const start = normalized.dateRange?.startDate?.trim();
  const end = normalized.dateRange?.endDate?.trim();
  if (start && end) {
    // Persist as YYYY-MM-DD if possible; otherwise fall back to the raw string.
    params.set("from", isIsoDateOnly(start) ? start : start.slice(0, 10));
    params.set("to", isIsoDateOnly(end) ? end : end.slice(0, 10));
  }

  if (normalized.showOnlyArchived) {
    params.set("archived", "1");
  }

  return params;
}

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const t = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

function applyFiltersFromUrlToContext(
  current: SurveyListFilters,
  next: SurveyListFilters,
  actions: ReturnType<typeof useSinkActions>
) {
  if ((current.searchString ?? null) !== (next.searchString ?? null)) {
    actions.setSurveySearchString((next.searchString ?? "") as any);
  }

  if (!arraysEqual(toSortedUniqueInts(current.selectedTagIds), next.selectedTagIds)) {
    actions.setSelectedTagIds(next.selectedTagIds);
  }

  if (
    !arraysEqual(toSortedUniqueInts(current.selectedFolderIds), next.selectedFolderIds)
  ) {
    actions.setSelectedFolderIds(next.selectedFolderIds);
  }

  if (!arraysEqual(toSortedUniqueInts(current.selectedSiteIds), next.selectedSiteIds)) {
    actions.setSelectedSiteIds(next.selectedSiteIds);
  }

  if (
    !arraysEqual(toSortedUniqueStrings(current.selectedStatuses), next.selectedStatuses)
  ) {
    actions.setSelectedStatuses(next.selectedStatuses);
  }

  if (
    !arraysEqual(
      toSortedUniqueStrings(current.selectedOrderTypes ?? []),
      next.selectedOrderTypes
    )
  ) {
    actions.setSelectedOrderTypes(next.selectedOrderTypes);
  }

  if (current.showOnlyArchived !== next.showOnlyArchived) {
    actions.setShowOnlyArchived(next.showOnlyArchived);
  }

  if (current.dateType !== next.dateType) {
    actions.setSurveyDateType(next.dateType);
  }

  const nextStart = next.dateRange.startDate ?? "";
  const nextEnd = next.dateRange.endDate ?? "";
  if (
    (current.dateRange.startDate ?? "") !== nextStart ||
    (current.dateRange.endDate ?? "") !== nextEnd
  ) {
    actions.setSurveyDateRange({ startDate: nextStart, endDate: nextEnd });
  }

  if (
    !arraysEqual(
      toSortedUniqueInts(current.selectedCreatedByUserIds),
      next.selectedCreatedByUserIds
    )
  ) {
    actions.setSelectedCreatedByUserIds(next.selectedCreatedByUserIds);
  }

  if (
    !arraysEqual(
      toSortedUniqueInts(current.selectedLockedByUserIds),
      next.selectedLockedByUserIds
    )
  ) {
    actions.setSelectedLockedByUserIds(next.selectedLockedByUserIds);
  }
}

export function useSurveyFilterParams() {
  const { surveyListFilters } = useSinkState();
  const actions = useSinkActions();
  const [searchParams, setSearchParams] = useSearchParams();

  const isInitializedRef = React.useRef(false);
  const lastAppliedUrlRef = React.useRef<string | null>(null);
  const latestFiltersRef = React.useRef<SurveyListFilters>(surveyListFilters as any);

  React.useEffect(() => {
    latestFiltersRef.current = surveyListFilters as any;
  }, [surveyListFilters]);

  // URL -> context (authoritative). useLayoutEffect so state updates before the
  // context->URL effect runs in the same commit; otherwise stale filters get written back.
  React.useLayoutEffect(() => {
    const urlString = searchParams.toString();

    if (lastAppliedUrlRef.current === urlString) {
      return;
    }

    const currentFilters = latestFiltersRef.current;
    const nextFilters = searchParamsToFilters(searchParams);

    if (
      filtersToSearchParams(nextFilters).toString() ===
      filtersToSearchParams(currentFilters).toString()
    ) {
      isInitializedRef.current = true;
      return;
    }

    applyFiltersFromUrlToContext(currentFilters, nextFilters, actions);

    isInitializedRef.current = true;
  }, [searchParams, actions]);

  const debouncedSearchString = useDebouncedValue(surveyListFilters.searchString, 300);

  // context -> URL whenever filters change.
  React.useEffect(() => {
    if (!isInitializedRef.current) return;

    const nextFilters = {
      ...(surveyListFilters as any),
      searchString: debouncedSearchString,
    } as SurveyListFilters;

    const nextParams = filtersToSearchParams(nextFilters);
    const nextString = nextParams.toString();
    const currentString = searchParams.toString();

    if (nextString === currentString) return;

    lastAppliedUrlRef.current = nextString;
    setSearchParams(nextParams, { replace: true });
  }, [
    surveyListFilters,
    debouncedSearchString,
    searchParams,
    setSearchParams,
  ]);
}

